import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../../../utils/supabase";
import { resolveInstrumentWorkflowMilestones } from "../../../utils/instrument-workflow";
import {
  filterSegmentsForAdmission,
  resolveAdmissionReceptionDate,
  resolveAdmissionStatus,
  valueWithinAdmission,
} from "../../../utils/instrument-admission";

const admissionKey = (poId, instrumentId) => `${poId || "sin-po"}:${instrumentId || "sin-instrumento"}`;

async function loadRemitosForAdmissions(rows) {
  const poIds = [...new Set((rows || []).map((row) => row.po_id).filter(Boolean))];
  const instrumentIds = [...new Set((rows || []).map((row) => row.instrument_id).filter(Boolean))];
  const byAdmission = new Map();
  if (!poIds.length || !instrumentIds.length) return { available: true, byAdmission };

  try {
    const { data: serviceOrders, error: serviceOrderError } = await supabase
      .from("service_orders")
      .select("id, po_id")
      .in("po_id", poIds);
    if (serviceOrderError) throw serviceOrderError;

    const poByServiceOrder = new Map((serviceOrders || []).map((row) => [String(row.id), row.po_id]));
    const serviceOrderIds = [...poByServiceOrder.keys()];
    if (!serviceOrderIds.length) return { available: true, byAdmission };

    const { data: remitos, error: remitoError } = await supabase
      .from("remitos")
      .select("id, os_id, date_out")
      .in("os_id", serviceOrderIds);
    if (remitoError) throw remitoError;

    const remitoById = new Map((remitos || []).map((row) => [String(row.id), row]));
    const remitoIds = [...remitoById.keys()];
    if (!remitoIds.length) return { available: true, byAdmission };

    const { data: items, error: itemError } = await supabase
      .from("remito_items")
      .select("remito_id, instrument_id")
      .in("remito_id", remitoIds)
      .in("instrument_id", instrumentIds);
    if (itemError) throw itemError;

    for (const item of items || []) {
      const remito = remitoById.get(String(item.remito_id));
      const poId = poByServiceOrder.get(String(remito?.os_id));
      if (!remito || !poId || !item.instrument_id) continue;
      const key = admissionKey(poId, item.instrument_id);
      const previous = byAdmission.get(key);
      if (!previous || String(remito.date_out || "") > String(previous.remito_date_out || "")) {
        byAdmission.set(key, {
          has_current_remito: true,
          remito_date_out: remito.date_out || null,
        });
      }
    }
    return { available: true, byAdmission };
  } catch (error) {
    console.warn("[InstrumentQuickSearch] remitos por ingreso", error?.message || error);
    return { available: false, byAdmission };
  }
}

// Pasos del circuito, en orden
const STEPS = [
  { key: "ingresado",  label: "Ingresado",    icon: "📋" },
  { key: "lab",        label: "En lab",        icon: "📥" },
  { key: "proceso",    label: "En proceso",    icon: "⚙️" },
  { key: "resultado",  label: "Resultado",     icon: "🔬" },
  { key: "revisado",   label: "Revisado",      icon: "📄" },
  { key: "remito",     label: "Para remitir",  icon: "📦" },
  { key: "entregado",  label: "Entregado",     icon: "🚚" },
];

const STATUS_LABELS = {
  pending_reception:             "Pendiente de recepción",
  active:                        "En depósito",
  in_progress:                   "En proceso",
  paused:                        "Pausado",
  calibrated:                    "Calibrado",
  rejected:                      "Rechazado",
  with_certificate:              "Con certificado",
  without_certificate:           "Sin certificado",
  reviewed:                      "Revisado por jefe",
  ready_to_remit:                "Listo para remitar",
  remitted:                      "Remitido",
  delivered:                     "Entregado",
  delivered_with_certificate:    "Entregado con cert.",
  delivered_without_certificate: "Entregado sin cert.",
  delivered_not_calibrated:      "Entregado no calibrado",
  delivered_not_repaired:        "Entregado no reparado",
  delivered_repaired:            "Entregado reparado",
  cancelled:                     "Cancelado",
};

const STATUS_THEME = {
  pending_reception:             { border: "border-gray-500/40",   bg: "bg-gray-500/10",   text: "text-gray-300",   pulse: "bg-gray-400" },
  active:                        { border: "border-slate-500/40",  bg: "bg-slate-500/10",  text: "text-slate-200",  pulse: "bg-slate-400" },
  in_progress:                   { border: "border-amber-500/40",  bg: "bg-amber-500/10",  text: "text-amber-200",  pulse: "bg-amber-400" },
  paused:                        { border: "border-yellow-500/40", bg: "bg-yellow-500/10", text: "text-yellow-200", pulse: "bg-yellow-400" },
  calibrated:                    { border: "border-emerald-500/40",bg: "bg-emerald-500/10",text: "text-emerald-200",pulse: "bg-emerald-400" },
  rejected:                      { border: "border-rose-500/40",   bg: "bg-rose-500/10",   text: "text-rose-200",   pulse: "bg-rose-400" },
  with_certificate:              { border: "border-cyan-500/40",   bg: "bg-cyan-500/10",   text: "text-cyan-200",   pulse: "bg-cyan-400" },
  without_certificate:           { border: "border-zinc-500/40",   bg: "bg-zinc-500/10",   text: "text-zinc-200",   pulse: "bg-zinc-400" },
  reviewed:                      { border: "border-violet-500/40", bg: "bg-violet-500/10", text: "text-violet-200", pulse: "bg-violet-400" },
  ready_to_remit:                { border: "border-sky-500/40",    bg: "bg-sky-500/10",    text: "text-sky-200",    pulse: "bg-sky-400" },
  remitted:                      { border: "border-teal-500/40",   bg: "bg-teal-500/10",   text: "text-teal-200",   pulse: "bg-teal-400" },
  delivered:                     { border: "border-indigo-500/40", bg: "bg-indigo-500/10", text: "text-indigo-200", pulse: "bg-indigo-400" },
  delivered_with_certificate:    { border: "border-indigo-500/40", bg: "bg-indigo-500/10", text: "text-indigo-200", pulse: "bg-indigo-400" },
  delivered_without_certificate: { border: "border-orange-500/40", bg: "bg-orange-500/10", text: "text-orange-200", pulse: "bg-orange-400" },
  delivered_not_calibrated:      { border: "border-orange-500/40", bg: "bg-orange-500/10", text: "text-orange-200", pulse: "bg-orange-400" },
  delivered_not_repaired:        { border: "border-orange-500/40", bg: "bg-orange-500/10", text: "text-orange-200", pulse: "bg-orange-400" },
  delivered_repaired:            { border: "border-indigo-500/40", bg: "bg-indigo-500/10", text: "text-indigo-200", pulse: "bg-indigo-400" },
  cancelled:                     { border: "border-zinc-600/40",   bg: "bg-zinc-600/10",   text: "text-zinc-300",   pulse: "bg-zinc-500" },
};

function getTheme(status) {
  return STATUS_THEME[status] || STATUS_THEME.active;
}

// Estados en los que el técnico ya produjo un resultado y solo falta la
// revisión del jefe. NO son un rechazo: "Sin certificado" es un resultado válido.
const RESULT_STATES = ["calibrated", "with_certificate", "without_certificate"];

// Cómo dibujar el recorrido:
//  doneUntil  → índice del último paso COMPLETADO (se pinta ✓ verde)
//  currentIdx → paso "actual" resaltado
//  mode       → 'active' (etapa en curso) | 'awaiting' (esperando al jefe)
//               | 'rejected' (rechazado) | 'none' (nada iniciado)
function getStepMeta(row) {
  const s = row?.status;
  if (s === "pending_reception") return { doneUntil: -1, currentIdx: -1, mode: "none" };
  if (["delivered", "remitted", "delivered_with_certificate", "delivered_without_certificate", "delivered_not_calibrated", "delivered_not_repaired", "delivered_repaired", "cancelled"].includes(s))
    return { doneUntil: 5, currentIdx: 6, mode: "active" };
  if (s === "ready_to_remit")            return { doneUntil: 4, currentIdx: 5, mode: "active" };
  if (s === "reviewed")                  return { doneUntil: 3, currentIdx: 4, mode: "active" };
  if (s === "rejected")                  return { doneUntil: 2, currentIdx: 3, mode: "rejected" };
  // Resultado listo (calibrado / con o sin certificado): "Resultado" queda ✓
  // y "Revisado" se muestra como el próximo paso, esperando al jefe.
  if (RESULT_STATES.includes(s))         return { doneUntil: 3, currentIdx: 4, mode: "awaiting" };
  if (["in_progress", "paused"].includes(s)) return { doneUntil: 1, currentIdx: 2, mode: "active" };
  if (!s || s === "active")              return { doneUntil: -1, currentIdx: 0, mode: "active" };
  if (row?.lab_entry_date)               return { doneUntil: 0, currentIdx: 1, mode: "active" };
  return { doneUntil: -1, currentIdx: 0, mode: "active" };
}

function fmtDate(v) {
  if (!v) return null;
  const [y, m, d] = String(v).slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

// entered_at más temprano cuyo status esté en la lista (segments viene ordenado asc)
function firstEnteredAt(segments, statuses) {
  for (const seg of segments || []) {
    if (statuses.includes(seg.status)) return seg.entered_at;
  }
  return null;
}

// Marca de tiempo para la línea: { dateStr: "21/04/26", timeStr: "14:30"|null }
function fmtStamp(v) {
  if (!v) return null;
  const s = String(v);
  const [y, m, d] = s.slice(0, 10).split("-");
  if (!y || !m || !d) return null;
  const dateStr = `${d}/${m}/${y.slice(2)}`;
  let timeStr = null;
  if (s.length > 10) {
    const dt = new Date(s);
    if (!Number.isNaN(dt.getTime())) {
      const hh = String(dt.getHours()).padStart(2, "0");
      const mm = String(dt.getMinutes()).padStart(2, "0");
      if (!(hh === "00" && mm === "00")) timeStr = `${hh}:${mm}`;
    }
  }
  return { dateStr, timeStr };
}

export default function InstrumentQuickSearch() {
  const [query, setQuery]     = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const timerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults([]);
      setSearched(false);
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSearch(q), 380);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  async function doSearch(q) {
    setLoading(true);
    setSearched(true);
    try {
      const isNum = /^\d+$/.test(q);
      const cols = "service_order_id, instrument_id, instrument_lab_id, instrument_tag, instrument_brand, instrument_model, instrument_serial, instrument_type, code, status, created_at, po_id, po_code";

      // Ordenamos por Lab ID para que, al buscar por Nº de OC/OS, salgan TODOS
      // los instrumentos de esa familia en orden (antes .limit(6) sin orden
      // recortaba y se "saltaba" los primeros).
      const queries = [
        supabase
          .from("v_service_orders_enriched")
          .select(cols)
          .ilike("code", `%${q}%`)
          .order("created_at", { ascending: false })
          .order("instrument_lab_id", { ascending: true })
          .limit(60),
      ];

      if (isNum) {
        queries.push(
          supabase
            .from("v_service_orders_enriched")
            .select(cols)
            .eq("instrument_lab_id", parseInt(q, 10))
            .order("created_at", { ascending: false })
            .order("instrument_lab_id", { ascending: true })
            .limit(6)
        );
      }

      const responses = await Promise.all(queries);

      // Deduplicar por instrument_id
      const seen = new Set();
      const baseRows = [];
      for (const { data } of responses) {
        for (const row of data || []) {
          if (row.instrument_id && !seen.has(row.instrument_id)) {
            seen.add(row.instrument_id);
            baseRows.push(row);
          }
        }
      }

      if (!baseRows.length) {
        setResults([]);
        return;
      }

      const instrIds = baseRows.map((r) => r.instrument_id);
      const poIds    = [...new Set(baseRows.map((r) => r.po_id).filter(Boolean))];

      // Enriquecer en paralelo: estado, técnico y remito real. La pertenencia
      // a remito es un hito independiente del estado actual del instrumento.
      const [instrResp, poResp, remitoScope] = await Promise.all([
        supabase
          .from("instruments")
          .select("id, status, technician_notes, rejection_reason, is_broken, lab_entry_date, reviewed_at, reception_date, departure_date, client:clients(id, name)")
          .in("id", instrIds),
        supabase
          .from("purchase_orders")
          .select("id, assigned_to, technicians:assigned_to(id, full_name)")
          .in("id", poIds),
        loadRemitosForAdmissions(baseRows),
      ]);

      const instrMap = Object.fromEntries((instrResp.data || []).map((r) => [r.id, r]));
      const techByPo = Object.fromEntries(
        (poResp.data || []).map((p) => [p.id, p.technicians?.full_name || null])
      );
      setResults(
        baseRows.map((r) => {
          const instrument = instrMap[r.instrument_id] || {};
          const remito = remitoScope.byAdmission.get(admissionKey(r.po_id, r.instrument_id));
          return {
            ...r,
            ...instrument,
            service_order_status: r.status,
            admission_started_at: r.created_at,
            reception_date: resolveAdmissionReceptionDate({
              serviceOrderCreatedAt: r.created_at,
              legacyReceptionDate: instrument.reception_date,
            }),
            departure_date: remito?.remito_date_out || null,
            technician_name: techByPo[r.po_id] || null,
            has_current_remito: remitoScope.available ? Boolean(remito) : null,
            remito_date_out: remito?.remito_date_out || null,
          };
        })
      );
    } catch (e) {
      console.error("[InstrumentQuickSearch]", e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  }

  const hasQuery = query.trim().length > 0;

  return (
    <div className="mb-5 rounded-2xl border border-sky-500/25 bg-sky-500/[0.05] p-4">
      {/* Título aclaratorio */}
      <div className="mb-3">
        <div className="text-base font-semibold text-sky-100 flex items-center gap-2">
          🔎 Consultá el estado de un instrumento
        </div>
        <div className="text-xs text-slate-400 mt-1 leading-relaxed">
          Ingresá el <b className="text-slate-200">ID del instrumento</b> o el <b className="text-slate-200">Nº de OS</b> y te muestra
          su recorrido y en qué etapa está (en depósito, calibrado, remitido, entregado…).
          <span className="text-slate-500"> · Esto no filtra órdenes — para eso usá los filtros de abajo.</span>
        </div>
      </div>

      {/* Barra de búsqueda */}
      <div className="relative">
        <span className="absolute inset-y-0 left-3.5 flex items-center text-slate-400 text-lg pointer-events-none select-none">
          🔍
        </span>
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          spellCheck={false}
          className="w-full rounded-xl bg-slate-800/60 border border-white/10 pl-11 pr-10 py-3.5 text-white text-base placeholder:text-slate-500 focus:outline-none focus:border-sky-500/50 focus:ring-1 focus:ring-sky-500/20 transition"
          placeholder="Escribí el ID del instrumento (ej: 1024)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {hasQuery && (
          <button
            className="absolute inset-y-0 right-3 flex items-center px-1 text-slate-500 hover:text-white transition"
            onClick={() => { setQuery(""); setResults([]); setSearched(false); inputRef.current?.focus(); }}
          >
            ✕
          </button>
        )}
      </div>

      {/* Resultados */}
      {searched && (
        <div className="mt-2">
          {loading ? (
            <div className="rounded-xl border border-white/10 bg-white/5 py-4 px-5 text-sm text-slate-400 flex items-center gap-2">
              <span className="animate-spin inline-block">⟳</span> Buscando...
            </div>
          ) : results.length === 0 ? (
            <div className="rounded-xl border border-white/10 bg-white/5 py-4 px-5 text-sm text-slate-400">
              No se encontraron instrumentos para{" "}
              <strong className="text-white">"{query}"</strong>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((r) => (
                <ResultCard key={r.instrument_id} row={r} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultCard({ row: initialRow }) {
  // `row` es estado local para poder refrescarlo sin volver a buscar. Si el
  // usuario hace una búsqueda nueva, se sincroniza con los datos del padre.
  const [row, setRow] = useState(initialRow);
  const [refreshing, setRefreshing] = useState(false);
  useEffect(() => { setRow(initialRow); }, [initialRow]);

  const instrName = [row.instrument_brand, row.instrument_model].filter(Boolean).join(" ")
    || row.instrument_tag
    || "Instrumento";

  // Segmentos del historial (status + entered_at). Se comparten entre la línea
  // de tiempo (marca de cada paso) y el cuadro de "Tiempos por etapa".
  const [segments, setSegments] = useState(null);
  useEffect(() => {
    let alive = true;
    setSegments(null);
    (async () => {
      try {
        const { data } = await supabase
          .from("v_instrument_status_segments")
          .select("status, entered_at, left_at")
          .eq("instrument_id", row.instrument_id)
          .order("entered_at", { ascending: true });
        if (alive) setSegments(data || []);
      } catch {
        if (alive) setSegments([]);
      }
    })();
    return () => { alive = false; };
  }, [row.instrument_id]);

  const cycleSegments = filterSegmentsForAdmission(segments, row.admission_started_at);
  const displayStatus = resolveAdmissionStatus({
    instrumentStatus: row.status,
    serviceOrderStatus: row.service_order_status,
    hasCurrentCycleTransition: cycleSegments.length > 0,
  });
  const displayRow = { ...row, status: displayStatus };
  const currentLabEntry = valueWithinAdmission(row.lab_entry_date, row.admission_started_at);
  const currentReviewedAt = valueWithinAdmission(row.reviewed_at, row.admission_started_at);
  const showCurrentCycleNotes = cycleSegments.length > 0;
  const meta = getStepMeta(displayRow);
  const theme = getTheme(displayStatus);
  const statusLabel = STATUS_LABELS[displayStatus] || displayStatus || "Sin definir";

  // Refrescar el estado del instrumento (por si cambió desde que se buscó):
  // reconsulta estado, técnico asignado y los segmentos del historial.
  async function handleRefresh() {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const [instResp, poResp, segResp, remitoScope] = await Promise.all([
        supabase
          .from("instruments")
          .select("id, status, technician_notes, rejection_reason, is_broken, lab_entry_date, reviewed_at, reception_date, departure_date, client:clients(id, name)")
          .eq("id", row.instrument_id)
          .single(),
        row.po_id
          ? supabase
              .from("purchase_orders")
              .select("assigned_to, technicians:assigned_to(id, full_name)")
              .eq("id", row.po_id)
              .single()
          : Promise.resolve({ data: null }),
        supabase
          .from("v_instrument_status_segments")
          .select("status, entered_at, left_at")
          .eq("instrument_id", row.instrument_id)
          .order("entered_at", { ascending: true }),
        loadRemitosForAdmissions([row]),
      ]);
      if (instResp?.data) {
        const remito = remitoScope.byAdmission.get(admissionKey(row.po_id, row.instrument_id));
        setRow((prev) => ({
          ...prev,
          ...instResp.data,
          reception_date: resolveAdmissionReceptionDate({
            serviceOrderCreatedAt: prev.admission_started_at,
            legacyReceptionDate: instResp.data.reception_date,
          }),
          departure_date: remito?.remito_date_out || null,
          technician_name: poResp?.data?.technicians?.full_name ?? prev.technician_name,
          has_current_remito: remitoScope.available ? Boolean(remito) : null,
          remito_date_out: remito?.remito_date_out || null,
        }));
      }
      setSegments(segResp.data || []);
    } catch (e) {
      console.error("[InstrumentQuickSearch] refresh", e);
    } finally {
      setRefreshing(false);
    }
  }

  // Marca de tiempo (fecha/hora) de cada paso de la línea. Se toma del historial
  // (entered_at, con hora) y, si no hay, de las fechas que ya guarda el instrumento.
  const stamps = {
    ingresado: row.reception_date || firstEnteredAt(cycleSegments, ["pending_reception", "active"]),
    lab:       currentLabEntry,
    proceso:   firstEnteredAt(cycleSegments, ["in_progress"]),
    resultado: firstEnteredAt(cycleSegments, ["calibrated", "rejected", "with_certificate", "without_certificate"]),
    revisado:  firstEnteredAt(cycleSegments, ["reviewed"]) || currentReviewedAt,
    remito:    firstEnteredAt(cycleSegments, ["remitted"]) || row.remito_date_out,
    entregado: firstEnteredAt(cycleSegments, [
      "delivered", "delivered_with_certificate", "delivered_without_certificate",
      "delivered_not_calibrated", "delivered_not_repaired", "delivered_repaired",
    ]) || row.departure_date,
  };
  const terminalMilestones = resolveInstrumentWorkflowMilestones({
    status: displayStatus,
    reviewedAt: currentReviewedAt,
    departureDate: row.departure_date,
    hasCurrentRemito: row.has_current_remito,
    remitoDateOut: row.remito_date_out,
    segments: cycleSegments,
  });
  stamps.revisado = terminalMilestones.stamps.revisado;
  stamps.remito = terminalMilestones.stamps.remito;
  stamps.entregado = terminalMilestones.stamps.entregado;
  const milestoneCompleted = terminalMilestones.completed;

  return (
    <div className={`rounded-xl border ${theme.border} ${theme.bg} p-4 space-y-4`}>

      {/* ── Encabezado ── */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-lg font-bold text-white tracking-tight">
              Lab #{row.instrument_lab_id ?? "—"}
            </span>
            {row.instrument_tag && (
              <span className="text-[11px] bg-white/10 px-2 py-0.5 rounded-full text-slate-300">
                TAG: {row.instrument_tag}
              </span>
            )}
            {row.instrument_type && (
              <span className="text-[11px] bg-white/10 px-2 py-0.5 rounded-full text-slate-400">
                {row.instrument_type}
              </span>
            )}
          </div>
          <div className="text-sm text-slate-200 mt-0.5 font-medium">{instrName}</div>
          {row.instrument_serial && (
            <div className="text-xs text-slate-500 mt-0.5">Serie: {row.instrument_serial}</div>
          )}
        </div>

        {/* Refrescar + badge de estado */}
        <div className="shrink-0 flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Actualizar el estado del instrumento"
            className="flex items-center gap-1 rounded-lg border border-white/15 bg-white/5 px-2.5 py-1.5 text-xs text-slate-300 hover:text-white hover:border-white/30 transition disabled:opacity-60"
          >
            <span className={refreshing ? "animate-spin inline-block" : "inline-block"}>⟳</span>
            {refreshing ? "Actualizando…" : "Refrescar"}
          </button>
          <div className={`flex items-center gap-1.5 rounded-lg border ${theme.border} px-3 py-1.5`}>
            <span className={`w-2 h-2 rounded-full ${theme.pulse} shrink-0`} />
            <span className={`text-sm font-semibold ${theme.text}`}>{statusLabel}</span>
          </div>
        </div>
      </div>

      {/* ── Info rápida ── */}
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {row.client?.name && (
          <span>
            <span className="text-slate-500">Cliente: </span>
            <span className="text-slate-200 font-medium">{row.client.name}</span>
          </span>
        )}
        {row.po_code && (
          <span>
            <span className="text-slate-500">OC: </span>
            <span className="text-slate-200 font-medium">{row.po_code}</span>
          </span>
        )}
        {row.code && (
          <span>
            <span className="text-slate-500">OS: </span>
            <span className="text-slate-200 font-medium">{row.code}</span>
          </span>
        )}
        {row.technician_name && (
          <span>
            <span className="text-slate-500">Técnico: </span>
            <span className="text-slate-200 font-medium">👤 {row.technician_name}</span>
          </span>
        )}
        {row.reception_date && (
          <span>
            <span className="text-slate-500">Ingresó: </span>
            <span className="text-slate-200">{fmtDate(row.reception_date)}</span>
          </span>
        )}
        {currentLabEntry && (
          <span>
            <span className="text-slate-500">Bajó al lab: </span>
            <span className="text-slate-200">{fmtDate(currentLabEntry)}</span>
          </span>
        )}
        {currentReviewedAt && (
          <span>
            <span className="text-slate-500">Revisado: </span>
            <span className="text-slate-200">{fmtDate(currentReviewedAt)}</span>
          </span>
        )}
      </div>

      {/* ── Pipeline visual ── */}
      <div className="overflow-x-auto">
      <div className="flex items-start min-w-[440px]">
        {STEPS.map((step, i) => {
          // Los tres hitos finales no se infieren por posición. Así un remito
          // previo a la revisión no pinta "Revisado", y una revisión posterior
          // tampoco borra el remito que realmente existe.
          const done = Object.prototype.hasOwnProperty.call(milestoneCompleted, step.key)
            ? milestoneCompleted[step.key]
            : i <= meta.doneUntil;
          const previousStep = STEPS[i - 1];
          const previousDone = i === 0 || (
            Object.prototype.hasOwnProperty.call(milestoneCompleted, previousStep.key)
              ? milestoneCompleted[previousStep.key]
              : (i - 1) <= meta.doneUntil
          );
          const current = i === meta.currentIdx;

          let circleClass, labelClass, icon;
          let label = step.key === "remito" && done ? "Remitido" : step.label;

          if (done) {
            circleClass = "border-2 border-emerald-500 bg-emerald-500/20 text-emerald-300";
            labelClass  = "text-emerald-500";
            icon        = "✓";
          } else if (current && meta.mode === "rejected") {
            circleClass = "border-2 border-rose-500 bg-rose-500/20 text-rose-300";
            labelClass  = "text-rose-400 font-semibold";
            icon        = "✕";
            label       = "Rechazado";
          } else if (current && meta.mode === "awaiting") {
            circleClass = "border-2 border-dashed border-amber-400/70 bg-amber-500/10 text-amber-300 animate-pulse";
            labelClass  = "text-amber-300 font-semibold";
            icon        = "⏳";
            label       = "Esperando revisión";
          } else if (current && meta.mode === "active") {
            circleClass = `border-2 ${theme.border} ${theme.bg} ${theme.text}`;
            labelClass  = `${theme.text} font-semibold`;
            icon        = step.icon;
          } else {
            circleClass = "border border-white/10 bg-white/5 text-slate-600";
            labelClass  = "text-slate-600";
            icon        = step.icon;
          }

          return (
            <React.Fragment key={step.key}>
              {i > 0 && (
                <div
                  className={`flex-1 h-0.5 mt-4 transition-colors ${
                    done && previousDone ? "bg-emerald-500/60" : "bg-white/10"
                  }`}
                />
              )}
              <div className="flex flex-col items-center gap-1 w-14">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${circleClass}`}
                >
                  {icon}
                </div>
                <span className={`text-[10px] text-center leading-tight ${labelClass}`}>
                  {label}
                </span>
                {(() => {
                  const st = fmtStamp(stamps[step.key]);
                  if (!st) return null;
                  return (
                    <span className="text-[9px] leading-tight text-center text-slate-500 tabular-nums">
                      {st.dateStr}
                      {st.timeStr && (<><br />{st.timeStr}</>)}
                    </span>
                  );
                })()}
              </div>
            </React.Fragment>
          );
        })}
      </div>
      </div>

      {/* ── Notas / Rechazo ── */}
      {showCurrentCycleNotes && row.rejection_reason && (
        <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-200 leading-relaxed">
          <span className="font-semibold">Motivo de rechazo: </span>
          {row.rejection_reason}
          {row.is_broken && (
            <span className="ml-2 font-semibold text-rose-300">· Equipo roto 🔧</span>
          )}
        </div>
      )}
      {showCurrentCycleNotes && row.technician_notes && (
        <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-200 leading-relaxed">
          <span className="font-semibold">Obs. técnico: </span>
          {row.technician_notes}
        </div>
      )}

      {/* ── Tiempos por etapa ── */}
      <StageTimesSection row={displayRow} segments={segments === null ? null : cycleSegments} />

      {/* ── Historial ── */}
      <HistorySection instrumentId={row.instrument_id} />
    </div>
  );
}

// ─────────────────────────────────────────────
//  TIEMPOS POR ETAPA
// ─────────────────────────────────────────────

// Qué estados suman a cada etapa con nombre
const STAGE_DEFS = [
  { key: "deposito",      label: "En depósito",            statuses: ["active"] },
  { key: "calibracion",   label: "Calibración (técnico)",  statuses: ["in_progress"] },
  // "Revisión (jefe)" no se suma por estados: se calcula aparte (resultado → revisado).
  { key: "revision",      label: "Revisión (jefe)" },
  { key: "espera_remito", label: "Espera de remito",       statuses: ["reviewed", "ready_to_remit"] },
  { key: "espera_retiro", label: "Espera de retiro",       statuses: ["remitted"] },
];

function fmtDuration(ms) {
  if (ms == null) return "—";
  const min = Math.round(ms / 60000);
  if (min < 1) return "< 1 min";
  if (min < 90) return `${min} min`;
  const h = ms / 3600000;
  if (h < 24) return `${h.toFixed(1)} h`;
  return `${(h / 24).toFixed(1)} días`;
}

function StageTimesSection({ row, segments }) {
  // Los segmentos llegan desde ResultCard (una sola consulta compartida).
  const loading = segments === null;

  if (loading) {
    return <div className="text-xs text-slate-500">Cargando tiempos…</div>;
  }

  // Si el instrumento figura como no recibido, no tiene sentido mostrar tiempos
  // de circuito (aunque el historial tenga un tramo previo de un ingreso revertido).
  if (row.status === "pending_reception") {
    return (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-1">
        <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
          ⏱️ Tiempos por etapa
        </div>
        <div className="text-[11px] text-amber-300/80 leading-relaxed">
          El instrumento figura como <b>no recibido</b> — todavía no ingresó al circuito, así que aún no hay tiempos por etapa.
        </div>
      </div>
    );
  }

  // Sumar duración (ms) por etapa, a partir de los segmentos del historial
  const totals = {};
  for (const seg of segments || []) {
    const start = Date.parse(seg.entered_at);
    const end = seg.left_at ? Date.parse(seg.left_at) : Date.now();
    if (Number.isNaN(start)) continue;
    const ms = Math.max(0, end - start);
    const def = STAGE_DEFS.find((d) => d.statuses?.includes(seg.status));
    if (def) totals[def.key] = (totals[def.key] || 0) + ms;
  }

  // Revisión (jefe): tiempo desde que el técnico dejó el resultado
  // (calibrado / con o sin certificado) hasta que el jefe marcó "revisado".
  // Si todavía no lo revisó, queda pendiente (—).
  {
    const RESULT_SET = ["calibrated", "with_certificate", "without_certificate"];
    let resultReadyAt = null, reviewedAt = null;
    for (const seg of segments || []) {
      const t = Date.parse(seg.entered_at);
      if (Number.isNaN(t)) continue;
      if (RESULT_SET.includes(seg.status) && resultReadyAt == null) resultReadyAt = t;
      if (seg.status === "reviewed" && reviewedAt == null) reviewedAt = t;
    }
    if (resultReadyAt != null && reviewedAt != null && reviewedAt >= resultReadyAt) {
      totals.revision = reviewedAt - resultReadyAt;
    }
  }

  const hasHistory = (segments || []).length > 0;

  // Total general (de lo que tengamos): ingreso → entrega
  let totalGeneral = null;
  if (hasHistory) {
    const first = Date.parse(segments[0].entered_at);
    const delivered = [...segments].reverse().find((s) => s.status === "delivered");
    const end = delivered ? Date.parse(delivered.entered_at) : null;
    if (end && !Number.isNaN(first)) totalGeneral = end - first;
  } else if (row.reception_date && row.departure_date) {
    // histórico: solo tiempo total aproximado con las fechas que ya teníamos
    const a = Date.parse(row.reception_date), b = Date.parse(row.departure_date);
    if (!Number.isNaN(a) && !Number.isNaN(b)) totalGeneral = b - a;
  }

  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
      <div className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">
        ⏱️ Tiempos por etapa
        {row.technician_name && (
          <span className="text-[11px] font-normal text-slate-500">· Técnico: {row.technician_name}</span>
        )}
      </div>

      {!hasHistory && (
        <div className="text-[11px] text-amber-300/80 leading-relaxed">
          Este instrumento es previo al seguimiento de tiempos — solo se muestra el total aproximado.
          El detalle por etapa se registra desde que pasa por el circuito.
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {STAGE_DEFS.map((d) => {
          const has = totals[d.key] != null;
          return (
            <div
              key={d.key}
              className={`rounded-md border px-2.5 py-1.5 ${
                has ? "border-white/15 bg-white/5" : "border-white/5 bg-transparent"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wide text-slate-500">{d.label}</div>
              <div className={`text-sm font-semibold ${has ? "text-slate-100" : "text-slate-600"}`}>
                {has ? fmtDuration(totals[d.key]) : "—"}
              </div>
            </div>
          );
        })}

        <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wide text-emerald-400/70">Total general</div>
          <div className="text-sm font-semibold text-emerald-300">
            {totalGeneral != null ? fmtDuration(totalGeneral) : "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
//  HISTORIAL DEL INSTRUMENTO
// ─────────────────────────────────────────────

const HISTORY_FILTERS = [
  { key: "all",         label: "Todo" },
  { key: "calibration", label: "Calibraciones" },
  { key: "certificate", label: "Certificados" },
  { key: "delivery",    label: "Remitos / Entregas" },
  { key: "os",          label: "Órdenes de servicio" },
];

const CALIB_STATUS_LABEL = {
  pending:     "Pendiente",
  in_progress: "En proceso",
  completed:   "Completada",
  cancelled:   "Cancelada",
};

function HistorySection({ instrumentId }) {
  const [open, setOpen]         = useState(false);
  const [loading, setLoading]   = useState(false);
  const [events, setEvents]     = useState([]);
  const [filter, setFilter]     = useState("all");
  const [yearFilter, setYearFilter] = useState("all");
  const loaded = useRef(false);

  async function loadHistory() {
    if (loaded.current) return;
    loaded.current = true;
    setLoading(true);
    try {
      const [calibResp, remResp, soResp] = await Promise.all([
        // Calibraciones históricas
        supabase
          .from("calibrations")
          .select("id, date, status, technician_name, technician_id, certificate_url, certificate_created_at, priority")
          .eq("instrument_id", instrumentId)
          .order("date", { ascending: false }),

        // Remitos / entregas
        supabase
          .from("remito_items")
          .select("id, remito_id, remitos(id, code, official_number, date_out, status)")
          .eq("instrument_id", instrumentId),

        // Órdenes de servicio
        supabase
          .from("service_orders")
          .select("id, code, status, created_at, technician_id")
          .eq("instrument_id", instrumentId)
          .order("created_at", { ascending: false }),
      ]);

      const all = [];

      // -- Calibraciones
      for (const c of calibResp.data || []) {
        all.push({
          type: "calibration",
          date: c.date,
          sortKey: c.date || "",
          icon: "⚙️",
          label: "Calibración",
          sub: c.technician_name || c.technician_id || "Técnico no registrado",
          badge: CALIB_STATUS_LABEL[c.status] || c.status || "—",
          badgeColor:
            c.status === "completed" ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10"
            : c.status === "in_progress" ? "text-amber-400 border-amber-500/30 bg-amber-500/10"
            : c.status === "cancelled" ? "text-rose-400 border-rose-500/30 bg-rose-500/10"
            : "text-slate-400 border-slate-500/30 bg-slate-500/10",
          extra: null,
        });

        if (c.certificate_url) {
          all.push({
            type: "certificate",
            date: c.certificate_created_at || c.date,
            sortKey: c.certificate_created_at || c.date || "",
            icon: "📜",
            label: "Certificado emitido",
            sub: c.technician_name || "—",
            badge: null,
            url: c.certificate_url,
            extra: null,
          });
        }
      }

      // -- Remitos
      for (const ri of remResp.data || []) {
        const rem = ri.remitos;
        if (!rem) continue;
        all.push({
          type: "delivery",
          date: rem.date_out,
          sortKey: rem.date_out || "",
          icon: "🚚",
          label: "Remito de entrega",
          sub: rem.official_number ? `N° ${rem.official_number}` : rem.code || "—",
          badge: rem.status || null,
          badgeColor: "text-teal-400 border-teal-500/30 bg-teal-500/10",
          extra: null,
        });
      }

      // -- Órdenes de servicio
      for (const so of soResp.data || []) {
        all.push({
          type: "os",
          date: so.created_at?.slice(0, 10),
          sortKey: so.created_at || "",
          icon: "📋",
          label: `OS ${so.code || so.id}`,
          sub: so.status || "—",
          badge: null,
          extra: null,
        });
      }

      // Ordenar por fecha descendente
      all.sort((a, b) => (b.sortKey > a.sortKey ? 1 : -1));
      setEvents(all);
    } catch (e) {
      console.error("[HistorySection]", e);
    } finally {
      setLoading(false);
    }
  }

  function handleToggle() {
    if (!open) loadHistory();
    setOpen((v) => !v);
  }

  // Años disponibles para filtrar
  const years = [...new Set(
    events.map((e) => e.date?.slice(0, 4)).filter(Boolean)
  )].sort((a, b) => b - a);

  const visible = events.filter((e) => {
    if (filter !== "all" && e.type !== filter) return false;
    if (yearFilter !== "all" && e.date?.slice(0, 4) !== yearFilter) return false;
    return true;
  });

  return (
    <div className="border-t border-white/10 pt-3">
      <button
        type="button"
        className="flex items-center gap-2 text-xs text-slate-400 hover:text-white transition"
        onClick={handleToggle}
      >
        <span className={`transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
        <span className="font-medium">📋 Historial del instrumento</span>
        {events.length > 0 && (
          <span className="rounded-full bg-white/10 px-1.5 py-0.5 text-[10px] text-slate-300">
            {events.length}
          </span>
        )}
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Filtros */}
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex flex-wrap gap-1">
              {HISTORY_FILTERS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setFilter(f.key)}
                  className={`px-2.5 py-1 rounded-full text-[11px] border transition ${
                    filter === f.key
                      ? "bg-sky-500/20 border-sky-500/50 text-sky-200"
                      : "border-white/10 text-slate-400 hover:text-white hover:border-white/20"
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            {years.length > 1 && (
              <select
                className="rounded-md border border-white/10 bg-slate-900 px-2 py-1 text-[11px] text-slate-300"
                value={yearFilter}
                onChange={(e) => setYearFilter(e.target.value)}
              >
                <option value="all">Todos los años</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            )}
          </div>

          {/* Timeline */}
          {loading ? (
            <div className="text-xs text-slate-500 py-2">Cargando historial...</div>
          ) : visible.length === 0 ? (
            <div className="text-xs text-slate-600 py-2">Sin registros para este filtro.</div>
          ) : (
            <div className="relative pl-5">
              {/* Línea vertical */}
              <div className="absolute left-[9px] top-0 bottom-0 w-px bg-white/10" />

              <div className="space-y-0">
                {visible.map((ev, i) => (
                  <div key={i} className="relative flex gap-3 pb-4">
                    {/* Dot */}
                    <div className="absolute left-[-11px] top-0.5 w-5 h-5 rounded-full bg-slate-900 border border-white/20 flex items-center justify-center text-[11px]">
                      {ev.icon}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div>
                          <span className="text-xs font-semibold text-white">{ev.label}</span>
                          {ev.sub && (
                            <span className="ml-2 text-[11px] text-slate-400">{ev.sub}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {ev.badge && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${ev.badgeColor}`}>
                              {ev.badge}
                            </span>
                          )}
                          {ev.date && (
                            <span className="text-[10px] text-slate-500">
                              {fmtDate(ev.date)}
                            </span>
                          )}
                        </div>
                      </div>
                      {ev.url && (
                        <a
                          href={ev.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-1 inline-flex items-center gap-1 text-[11px] text-sky-400 hover:text-sky-300 underline underline-offset-2"
                        >
                          📄 Ver certificado
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
